import { get } from "aws-amplify/api";
import { fetchAuthSession, getCurrentUser, signOut, signInWithRedirect } from "aws-amplify/auth";
import { Hub } from '@aws-amplify/core';
import { Cache } from '@aws-amplify/core';
import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button, Col, Form, Nav, Navbar, NavDropdown, Row } from "react-bootstrap";
import { configure, HotKeys } from "react-hotkeys";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Creatable from "react-select/creatable";
import "./App.css";
import config from "./config";
import "./containers/common/Detail.css";
import Routes from "./Routes";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAllLookups } from "./hooks/useAllLookups";

const queryClient = new QueryClient();

class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }


  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }


  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }


  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <p style={{ color: 'red' }}>{this.state.error?.message}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload Application
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}


const AppWrapper = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </GlobalErrorBoundary>
);
export default AppWrapper;


const keyMap = {
  QUICK_LOOKUP: "alt+l",
};
configure({ ignoreTags: [] });


const App = () => {
  const [userPermissions, setUserPermissions] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [, setPropertyKey] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const isCheckingRef = useRef(false);
  const userRef = useRef(null);

  const history = useNavigate();
  const location = useLocation();

  const normalizeTrustProof = useCallback((value) => {
    if (typeof value === "string") {
      const normalizedValue = value.trim().toLowerCase();
      return normalizedValue === "true" || normalizedValue === "1";
    }
    return value === true || value === 1;
  }, []);

  const isLookupsEnabled = isAuthenticated && (selectedApp === "cityscope" || selectedApp === "slm");
  const lookupsResult = useAllLookups(isLookupsEnabled);


  const {
    lookups,
    isLoading: isLookupsLoading,
    isError: isLookupsError
  } = lookupsResult;


  const userHasAuthenticated = useCallback((authenticated) => {
    setIsAuthenticated(authenticated);
  }, []);


  const handleLogin = async (event) => {
    setIsAuthenticating(true);
    signInWithRedirect();
  };


  const handleLogout = async (event) => {
    await signOut();
    userHasAuthenticated(false);
    setSelectedApp(null);
    console.log("HandlelogOUT Authenticating changed to = " + isAuthenticating + " (App.js)");
  };


  const getPropertySearchHistory = useCallback(async (filter) => {
    try {
      console.log("In get user search history");
      console.log(filter);
      get({
        apiName: "colin",
        path: "/propertysearches",
        options: {
          queryParams: filter,
        },
      }).response.then(({ body }) => body.json()).then((returnedPropertyOptions) => {
        console.log(returnedPropertyOptions);
        if (returnedPropertyOptions) {
          setPropertyOptions(returnedPropertyOptions);
        }
      });
    } catch (e) {
      console.log("Error getting property search history " + e);
    }
  }, []);


  const getUserPermissions = useCallback(async (filter) => {
    if (!filter || !filter.userName) {
      console.warn("Permissions fetch aborted: Invalid filter/userName.");
      return;
    }

    try {
      console.log("In get user permissions");
      console.log(filter);

      const { body } = await get({
        apiName: "colin",
        path: "/users",
        options: {
          queryParams: { userName: filter.userName },
        }
      }).response;

      const userPermissionsData = await body.json();
      console.log("Permissions RAW Data:", userPermissionsData);

      if (!userPermissionsData) {
        console.error("Backend returned NULL for user permissions.");
        setUserPermissions({ trust_proof: false, error: "No user record found" });
        return;
      }

      if (Array.isArray(userPermissionsData) &&
        userPermissionsData.length > 0 &&
        userPermissionsData[0].trust_proof !== undefined) {
        const permissionsObject = userPermissionsData[0];
        console.log("Permissions SUCCESS:", permissionsObject);
        setUserPermissions({
          userPermissions: permissionsObject,
          trust_proof: normalizeTrustProof(permissionsObject.trust_proof),
        });

      } else {
        console.log("No valid user permissions found for the current user/filter.");
        setUserPermissions({ trust_proof: false });
      }
    } catch (e) {
      console.error("Error getting user permissions: ", e);
      setUserPermissions({ trust_proof: false });
    }
  }, [normalizeTrustProof]);


  const addNewPropertyToSearch = useCallback(
    (propertyKey) => {
      setPropertyKey(propertyKey);
      let localPropertyOptions = [...propertyOptions];
      localPropertyOptions.push({
        value: propertyKey,
        label: propertyKey,
      });
      setPropertyOptions(localPropertyOptions);
    },
    [propertyOptions]
  );


  const focusOnQuickLookup = useCallback(() => {
    console.log("in focusOnQuickLookup");
    document.getElementById("quickLookup").focus();
  }, []);


  const handlePropertyInputChange = useCallback(
    (event) => {
      setPropertyKey(event);
      let localPropertyOptions = [...propertyOptions];

      let index = localPropertyOptions.findIndex((option) => option.value === event.value);
      if (index >= 0) {
        localPropertyOptions.splice(index, 1);
      }
      localPropertyOptions.unshift({
        value: event,
        label: event,
      });
      setPropertyOptions(localPropertyOptions);

      history("/properties/" + event.toUpperCase());
    },
    [history, propertyOptions]
  );


  const handlePropertyChange = useCallback(
    (selectedProperty) => {
      setSelectedProperty(null);
      let localPropertyOptions = [...propertyOptions];

      let index = localPropertyOptions.findIndex(
        (option) => option.value === selectedProperty.value
      );
      console.log(index);
      if (index >= 0) {
        console.log("Modifying the list");
        localPropertyOptions.splice(index, 1);
      }
      localPropertyOptions.unshift(selectedProperty);
      setPropertyOptions(localPropertyOptions);

      setPropertyKey(selectedProperty.label.toUpperCase());

      history("/properties/" + selectedProperty.label.toUpperCase());
    },
    [history, propertyOptions]
  );


  const handleSelectCityScope = useCallback(() => {
    getPropertySearchHistory({ user: user });
    getUserPermissions({ userName: user });
    setSelectedApp("cityscope");
    history("/properties");
  }, [user, getPropertySearchHistory, getUserPermissions, history]);

  const handleSelectSLM = useCallback(() => {
    setSelectedApp("slm");
    history("/slmaddresses");
  }, [history]);


  const handleSelectCoapiDemo = useCallback(() => {
    setSelectedApp("coapidemo");
    history("/coapidemo");
  }, [history]);


  const handleAppReset = useCallback(() => {
    setSelectedApp(null);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const path = location.pathname.toLowerCase();
    if (path === "/") {
      return;
    }

    if (path === "/coapidemo") {
      if (selectedApp !== "coapidemo") {
        setSelectedApp("coapidemo");
      }
      return;
    }

    if (path === "/iscompanykeywords") {
      return;
    }

    if (path.startsWith("/slm")) {
      if (selectedApp !== "slm") {
        setSelectedApp("slm");
      }
      return;
    }

    if (selectedApp !== "cityscope") {
      setSelectedApp("cityscope");
    }
  }, [isAuthenticated, location.pathname, selectedApp]);


  const appSelect = () => {
    return (
      <div className="App container-fluid" style={{ margin: "100px auto" }}>
        <br />
        <div className="bg">
          <img src="/../cotality_background_1.png" alt="" />
        </div>
        <Form>
          <Row className="justify-content-center d-flex align-items-center">
            <Col xs="auto" className="mb-2">
              <Button
                variant="danger"
                onClick={handleSelectCityScope}
                style={{ width: "270px", paddingTop: "4px", paddingBottom: "4px", zIndex: 5 }}
              >
                Cityscope
              </Button>
            </Col>

            <Col xs="auto" className="mb-2">
              <Button
                variant="danger"
                onClick={handleSelectSLM}
                style={{ width: "270px", paddingTop: "4px", paddingBottom: "4px", zIndex: 5 }}
              >
                SLM
              </Button>
            </Col>
            {/* <Col sm={2} md={2} lg={2}><Link to="/coapidemo"><Button variant="danger"  onClick={handleSelectCoapiDemo}  block>COAPI Demo</Button></Link></Col> */}
          </Row>
        </Form>
      </div>
    );
  };


  const getChildProps = () => {
    return {
      isAuthenticated: isAuthenticated,
      userHasAuthenticated: userHasAuthenticated,
      user: user,
      userPermissions: userPermissions,
      addNewPropertyToSearch: addNewPropertyToSearch,
      handleAppReset: handleAppReset,

      // --- MAPPING LOOKUP DATA FROM HOOK ---
      sourcecategoryOptions: lookups.sourcecategoryOptions || [],
      statuscategoryOptions: lookups.statuscategoryOptions || [],
      spaceusecategoryOptions: lookups.spaceusecategoryOptions || [],
      broadspaceusecategoryOptions: lookups.broadspaceusecategoryOptions || [],
      zoningcategoryOptions: lookups.zoningcategoryOptions || [],
      qualitytypecategoryOptions: lookups.qualitytypecategoryOptions || [],
      titlebasiscategoryOptions: lookups.titlebasiscategoryOptions || [],
      salemethodcategoryOptions: lookups.salemethodcategoryOptions || [],
      saledescribercategoryOptions: lookups.saledescribercategoryOptions || [],
      incomebasiscategoryOptions: lookups.incomebasiscategoryOptions || [],
      areabasiscategoryOptions: lookups.areabasiscategoryOptions || [],
      carspacecategoryOptions: lookups.carspacecategoryOptions || [],
      saleTypes: lookups.saleTypes || [],
      leasemethodcategoryOptions: lookups.leasemethodcategoryOptions || [],
      leasestatuscategoryOptions: lookups.leasestatuscategoryOptions || [],
      leaselevelcategoryOptions: lookups.leaselevelcategoryOptions || [],
      leaseoutgoingscategoryOptions: lookups.leaseoutgoingscategoryOptions || [],
      rentperannumbasiscategoryOptions: lookups.rentperannumbasiscategoryOptions || [],
      rentpersqmbasiscategoryOptions: lookups.rentpersqmbasiscategoryOptions || [],
      leaselreviewbasiscategoryOptions: lookups.leaselreviewbasiscategoryOptions || [],
      leaseTypes: lookups.leaseTypes || [],
      slmlookups: lookups.slmlookups || [],
      streetTypes: lookups.streetTypes || [],
      unitTypes: lookups.unitTypes || [],
      categoryGroups: lookups.categoryGroups || [],
      subRegionOptions: lookups.subRegionOptions || [],
      reviewTypeOptions: lookups.reviewTypeOptions || [],

      liftOperatorOptions: lookups.liftOperatorOptions || [],
      articleCategories: lookups.articleCategories || [],
      fileTypes: lookups.fileTypes || [],
      withholdingReasons: lookups.withholdingReasons || [],
      buildingGradeSourceOptions: lookups.buildingGradeSourceOptions || [],
      buildingGradeOptions: lookups.buildingGradeOptions || [],
      classificationOptions: lookups.classificationOptions || [],
      leaseClassificationOptions: lookups.leaseClassificationOptions || [],
      countryOptions: lookups.countryOptions || [],
      countryCodeOptions: lookups.countryCodeOptions || [],
      salutationOptions: lookups.salutationOptions || [],
      entityTypeOptions: lookups.entityTypeOptions || [],
      positionOptions: lookups.positionOptions || [],
      firstNames: lookups.firstNames || [],
      // --- END MAPPING ---
    };
  };


  const processUserDetails = useCallback(async () => {
    try {
      const userDetails = await getCurrentUser();

      if (userDetails && userDetails.username) {
        let rawName = userDetails.username;
        let strippedName = rawName.replace(/^ISC_/, "");
        const finalUsername = strippedName.split('@')[0];

        console.log("Format matched for API:", finalUsername);
        setUser(finalUsername);
        userRef.current = finalUsername;
        return finalUsername;
      }
    } catch (e) {
      console.log("Error in processUserDetails:", e);
      return null;
    }
  }, []);


  useEffect(() => {
    const listener = (data) => {
      const { channel, payload } = data;
      if (channel === "auth" && payload.event === "signInWithRedirect") {
        console.log("Auth event: " + payload.event);
        processUserDetails();
      }
    };

    const hubListenerCancel = Hub.listen("auth", listener);

    async function checkInitialSession() {
      if (isCheckingRef.current || userRef.current) return;
      isCheckingRef.current = true;
      console.log("Checking Initial Session...");
      setIsAuthenticating(true);

      try {
        await fetchAuthSession();
        const username = await processUserDetails();
        const path = window.location.pathname;

        if (username) {
          userHasAuthenticated(true);
          if (path === "/") {
            handleAppReset();
          } else if (path.includes("slm")) {
            setSelectedApp("slm");
          } else if (path === "/coapidemo") {
            setSelectedApp("coapidemo");
          } else {
            getPropertySearchHistory({ user: username });
            getUserPermissions({ userName: username });
            setSelectedApp("cityscope");
          }
        }
      } catch (e) {
        console.error("Initial authentication check failed:", e);
        userHasAuthenticated(false);
      } finally {
        isCheckingRef.current = false;
        setIsAuthenticating(false);
      }
    }

    checkInitialSession();
    Cache.configure();

    return () => {
      hubListenerCancel();
    };

  }, [processUserDetails, userHasAuthenticated, handleAppReset, handleSelectCityScope, handleSelectCoapiDemo, handleSelectSLM, getPropertySearchHistory, getUserPermissions]);


  useEffect(() => {
    if (!user) return;

    if (user) {
      const filter = { userName: user };
      getUserPermissions(filter);
    } else {
      setUserPermissions(null);
    }
  }, [user, getUserPermissions, setUserPermissions]);

  useEffect(() => {
    if (!isAuthenticated || !user || selectedApp !== "cityscope") {
      return;
    }

    getUserPermissions({ userName: user });
  }, [isAuthenticated, user, selectedApp, location.pathname, getUserPermissions]);


  const renderCityscope = (childProps) => {
    const handlers = {
      QUICK_LOOKUP: focusOnQuickLookup,
    };
    return (
      <div className="App container-fluid" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <HotKeys keyMap={keyMap} handlers={handlers}>
          <Navbar collapseOnSelect expand="lg" bg="light" variant="light" sticky="top" className="d-print-none">
                <Navbar.Brand>
                  <Link to="/">
                    <img src="/cotality_logo.png" alt="Logo" className="nav-logo-fixed" />
                  </Link>
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                <Navbar.Collapse id="responsive-navbar-nav">
                  <Nav className="mr-auto">
                    <Nav.Link as={Link} to="/properties">Properties </Nav.Link>
                    <Nav.Link as={Link} to="/leases">Tenants </Nav.Link>
                    <Nav.Link as={Link} to="/owners">Owners</Nav.Link>
                    <Nav.Link as={Link} to="/entities">Entities</Nav.Link>
                    <Nav.Link as={Link} to="/headoffices">Head Offices</Nav.Link>
                    <NavDropdown title="Proofing" id="basic-nav-dropdown">
                      <NavDropdown.Item as={Link} to="/proofing/WithholdingProofing">HOLD Checks</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/proofing">Other Proofing</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/proofing/ad-hoc">Ad-hoc Proofing Queues</NavDropdown.Item>
                    </NavDropdown>
                    <NavDropdown title="Queues" id="basic-nav-dropdown">
                      <NavDropdown.Item as={Link} to="/queues/asicsearches"> ASIC Searches</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/queues/salesheets">Sale Sheet Follow Up</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/queues/queries">Queries</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/queues/salesfeed">RP Sales Feed</NavDropdown.Item>
                    </NavDropdown>
                    <NavDropdown title="Lookups" id="basic-nav-dropdown">
                      <NavDropdown.Item as={Link} to="/cityscopes">Cityscopes</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/articlecategories">Article Categories</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/countries">Countries</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/cpirates">CPI Rates</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/ausuburbs">AU Suburbs</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/nzsuburbs">NZ Suburbs</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/positions">Positions</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/salutations">Salutations</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/iscompanykeywords">Is Company Keywords</NavDropdown.Item>
                      {userPermissions?.trust_proof && (
                        <NavDropdown.Item as={Link} to="/users">Users</NavDropdown.Item>
                      )}
                    </NavDropdown>
                    <Nav.Link as={Link} to="/mergersubs"> Mergers &amp; Subs</Nav.Link>
                    <NavDropdown title="Utilities" id="basic-nav-dropdown">
                      <NavDropdown.Item as={Link} to="/walkcopy"> Walk Copy</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/intropgx"> Newsletters</NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/mergersubsexport"> Mergers &amp; Subs Export</NavDropdown.Item>
                    </NavDropdown>
                  </Nav>
                </Navbar.Collapse>
                {config.env !== "prod" && (
                  <h2>
                    <font color="red">UAT NOT LIVE</font>
                  </h2>
                )}
                <Nav className="justify-content-end">
                  <Nav.Item>
                    {isAuthenticated && user ? (
                      <Nav.Link onClick={handleLogout}>Logout</Nav.Link>
                    ) : (
                      <Nav.Link onClick={handleLogin}>Login</Nav.Link>
                    )}
                  </Nav.Item>
                </Nav>
          </Navbar>

          <Row>
            <Col>
              <Form>
                <Row>
                  <Col sm={4} md={4} lg={2}>
                    <Creatable
                      onCreateOption={handlePropertyInputChange}
                      value={selectedProperty}
                      onChange={handlePropertyChange}
                      options={propertyOptions}
                      placeholder="Quick Lookup"
                      tabindex="0"
                      inputId="quickLookup"
                      className="quickLookup"
                    />
                  </Col>
                </Row>
              </Form>

              <Routes childProps={childProps} />
            </Col>
          </Row>
        </HotKeys>
      </div>
    );
  };

  const renderSLM = (childProps) => {
    console.log("In renderSLM");

    // Cache.setItem('key', 'value');
    return (
      <div className="App container-fluid">
        <div
          className="App container-fluid"
          style={{
            width: "100%",
            maxWidth: "100%",
            margin: "0 auto",
            position: "relative",
            paddingLeft: "0",
            paddingRight: "0",
          }}
        >

          <Navbar collapseOnSelect expand="lg" variant="dark" bg="dark" sticky="top" style={{ zIndex: 10 }} className="d-print-none">
            <Navbar.Brand>
              <Link to="/">
                <img
                  src="/cotality_logo_white.png"
                  alt="Logo"
                  className="slm-nav-logo-fixed"
                />
              </Link>
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="responsive-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="mr-auto">
                <Nav.Link as={Link} to="/slmaddresses">Addresses</Nav.Link>
                <Nav.Link as={Link} to="/slmsales">Sales</Nav.Link>
                <Nav.Link as={Link} to="/slmleases">Leases</Nav.Link>
                <NavDropdown title="Lookups" id="basic-nav-dropdown">
                  <NavDropdown.Item as={Link} to="/slmcategories">Categories</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/slmsuburbs">Suburbs Add/Edit</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/slmrcasuppressionlist">RCA Manual Suppressions</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/iscompanykeywords">RCA Keyword Suppressions</NavDropdown.Item>
                </NavDropdown>
              </Nav>
              {config.env !== "prod" && (
                <h2>
                  <font color="red">UAT NOT LIVE</font>
                </h2>
              )}
              <Nav className="justify-content-end">
                <Nav.Item>
                  {isAuthenticated && user ? (
                    <Nav.Link onClick={handleLogout}>Logout</Nav.Link>
                  ) : (
                    <Nav.Link onClick={handleLogin}>Login</Nav.Link>
                  )}
                </Nav.Item>
              </Nav>
            </Navbar.Collapse>
          </Navbar>
          <br />
          <div>
            <div>
              <br />
              <Routes childProps={childProps} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCoapiDemo = (childProps) => {
    console.log("In renderCoapiDemo");
    return (
      <div className="App container-fluid">
        <Routes childProps={childProps} />
      </div>
    );
  };

  if (isLookupsError) {
    return (
      <div className="container text-center" style={{ marginTop: '50px' }}>
        <h3>⚠️ Warning: Some data failed to load</h3>
        <p>The application may not function correctly. Check your connection.</p>
        <button className="btn btn-warning" onClick={() => lookupsResult.refetch()}>
          Retry Loading Data
        </button>
        <hr />
        {appSelect()}
      </div>
    );
  }

  if (isAuthenticating) {
    return (
      <div className="App container-fluid" style={{ textAlign: 'center', paddingTop: '50px' }}>
      </div>
    );
  }

  if (isAuthenticated && user) {
    if (!userPermissions || (isLookupsLoading && window.location.hostname !== "localhost")) {
      return (
        <div className="App container-fluid" style={{ textAlign: 'center', paddingTop: '50px' }}>
          {isLookupsError && <p style={{ color: 'red', marginTop: '20px' }}>Error loading essential lookups. Please refresh.</p>}
        </div >
      );
    }

    const childProps = getChildProps();

    if (!selectedApp) {
      return appSelect(childProps);
    }
    if (selectedApp === "cityscope") {
      return renderCityscope(childProps);
    }
    if (selectedApp === "slm") {
      return renderSLM(childProps);
    }
    if (selectedApp === "coapidemo") {
      return renderCoapiDemo(childProps);
    }
  } else {
    return (
      <div className="App container-fluid">
        <div className="bg">
          <img src="/../cotality_background_2.png" alt="" />
        </div>
        <Navbar collapseOnSelect expand="lg" variant="light" bg="light">
          <Navbar.Brand>
            <Link to="/">
              <img src="/cotality_logo.png" alt="Logo" className="nav-logo-fixed" />
            </Link>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="mr-auto"></Nav>
            <Nav className="justify-content-end">
              <Nav.Item>
                {isAuthenticated && user ? (
                  <Nav.Link onClick={handleLogout}>Logout</Nav.Link>
                ) : (
                  <Nav.Link onClick={handleLogin}>Login</Nav.Link>
                )}
              </Nav.Item>
            </Nav>
          </Navbar.Collapse>
        </Navbar>
      </div>
    );
  }
};
